import { useDataModel } from '@thd-nucleus/data-sources';
import { DRAWER_FLOWS } from '../../../constants/drawerFlows';
import { useOutOfStockRecommendations } from './useOutOfStockRecommendations';

/**
 * Picks the product source for the swap drawer based on the flow. Only the active flow's
 * queries run; the others are skipped.
 * - default: packageSwapRecs.
 * - fitOptions: searchModel by navParam. Search does not reliably populate `buyable`, so only
 *   the current item and discontinued items are dropped.
 * - oosRecommendations: more-like-this recs filtered to deliverable items (useOutOfStockRecommendations).
 */
export const useSwapProducts = ({
  flow,
  itemId,
  storeId,
  zipCode,
  channel,
  promoId,
  filterResponse,
  navParam,
  fetch
}) => {
  const isFitOptionsFlow = flow === DRAWER_FLOWS.FIT_OPTIONS;
  const isOutOfStockFlow = flow === DRAWER_FLOWS.OOS_RECOMMENDATIONS;
  const isRecsFlow = !isFitOptionsFlow && !isOutOfStockFlow;

  const recs = useDataModel('packageSwapRecs', {
    variables: {
      itemId,
      storeId,
      promoId,
      filterResponse
    },
    ssr: false,
    skip: !isRecsFlow || !itemId || !fetch
  });

  const search = useDataModel('searchModel', {
    variables: {
      navParam,
      storeId,
      storefilter: 'ALL'
    },
    ssr: false,
    skip: !isFitOptionsFlow || !navParam || !fetch
  });

  const outOfStock = useOutOfStockRecommendations({
    itemId,
    storeId,
    zipCode,
    channel,
    skip: !isOutOfStockFlow || !itemId || !fetch
  });

  if (isOutOfStockFlow) {
    return {
      // The drawer opens immediately and shows a skeleton while the recs chain resolves
      hasResponse: fetch,
      products: outOfStock.products,
      loading: outOfStock.loading,
      error: undefined,
      currentItemSpecificationGroup: outOfStock.currentItemSpecificationGroup
    };
  }

  if (isFitOptionsFlow) {
    return {
      // Without a navParam there is nothing to search; render the empty state rather than nothing
      hasResponse: Boolean(search.data) || (!navParam && fetch),
      products: search.data?.searchModel?.products?.filter(
        (product) => product?.itemId !== itemId && !product?.availabilityType?.discontinued
      ),
      loading: search.loading,
      error: search.error,
      currentItemSpecificationGroup: null
    };
  }

  return {
    hasResponse: Boolean(recs.data),
    products: recs.data?.packageSwapRecs?.products,
    loading: recs.loading,
    error: recs.error,
    currentItemSpecificationGroup: null
  };
};
